import minimalImg from "../../assets/setup architecture.png"
import { FiChevronRight } from "react-icons/fi";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

export function renderMinimal () {

  // Reusable CodeBlock component
  const CodeBlock = ({ code }) => {
    const [copied, setCopied] = React.useState(false);
  
    const copyToClipboard = () => {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
  
    return (
      <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4 transition-opacity duration-300">
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={copyToClipboard}
            className="p-1.5 rounded text-white"
            title="Copy to clipboard"
          >
            {copied ? (
              <span className="text-green-400 text-lg leading-none">✔</span>
            ) : (
              <ClipboardIcon className="w-4 h-4" />
            )}
          </button>
        </div>
  
        <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
          {code}
        </pre>
      </div>
    );
  };

  return (
  <main className="py-10" id="main-content-area">
    <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
      <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
      <FiChevronRight className="text-[14px]" />
      <span>Deployment and Configuration</span>
      <FiChevronRight className="text-[14px]" />
      <span className="text-[#1b140d] dark:text-white font-medium">Minimal Setup Guide</span>
    </nav>

    <div className="transition-opacity duration-300" id="apm-content">
      <div className="border-b border-border-color dark:border-border-dark pb-8 mb-10">
        <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
          Netraa APM
        </span>
        <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">Netraa {""}</span>On Prem Installation Guide<br/> for Demo/Minimal Setup
        </h1>

        <div className="mt-6 flex flex-col md:flex-row md:items-start gap-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-6">
          <div className="flex-1">
            <h4 className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">info</span>
              Setup Context
            </h4>
            <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200 space-y-1 ml-1">
              <li><strong>Operating System:</strong> Debian based OS (Ubuntu, Mint, etc.)</li>
              <li><strong>Container Runtime:</strong> Docker & Docker Compose</li>
            </ul>
            <div className="mt-3 p-3 bg-white dark:bg-black/20 rounded-lg border border-blue-100 dark:border-blue-800/50">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                <strong>NOTE:</strong> This guide is specifically tailored for Debian-based systems. For RHEL/CentOS, please refer to the Enterprise RHEL Guide.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Architecture */}
      <section className="scroll-mt-24" id="architecture">
        <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6">
          Minimal Setup Architecture
        </h2>
        <div className="p-8 bg-surface-light dark:bg-surface-dark border border-dashed border-border-color dark:border-border-dark rounded-xl flex items-center justify-center min-h-[160px]">
          <img
            src={minimalImg} // your image path
            alt="Architecture Diagram"
            className="h-auto w-auto object-contain"
          />
        </div>
      </section>

      {/* Prerequisites */}
      <section className="scroll-mt-24" id="prerequisites">
        <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3 mt-8">
          <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900/50">
            <span className="material-symbols-outlined text-[24px]">checklist</span>
          </span>
          1. Common Prerequisites
        </h2>

        <div className="space-y-2">
          <div>
            <h3 className="text-lg font-bold text-text-main dark:text-gray-200 mb-3">System Requirements</h3>
            <p className="text-sm text-text-muted mb-4">
              Ensure your environment meets the following baseline before proceeding. A dedicated <code className="bg-gray-100 dark:bg-white/10 px-1 py-0.5 rounded text-xs font-mono">/data</code> partition is strictly required for persistent storage.
            </p>
            <ul className="list-disc ml-6 text-sm text-text-muted space-y-2 mb-6">
              <li>Linux OS</li>
              <li>Root or sudo access</li>
              <li>
                A separate disk partition is required for running the software (for example: <code className="bg-gray-100 dark:bg-white/10 px-1 py-0.5 rounded text-xs font-mono">/data</code>)
              </li>
              <li>All offline installation bundles must be copied to the target server(s)</li>
            </ul>
            <div className="overflow-x-auto border border-border-color dark:border-border-dark rounded-xl">
              <table className="w-auto text-left border-collapse">
                <thead className="divide-y divide-border-color dark:divide-border-dark text-[11px]">
                  <tr>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">Deployment Scale</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">Component</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted text-center">Qty</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">OS</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">CPU (Cores)</th>
                    <th className="px-3text-sm text-text-muted mb-2 font-medium py-4 text-[10px] font-bold uppercase text-text-muted">Memory (GB)</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">Storage</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">Source</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">Ports</th>
                    <th className="px-3 py-4 text-[10px] font-bold uppercase text-text-muted">Software</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-color dark:divide-border-dark text-[11px]">
                  <tr className="bg-white dark:bg-transparent">
                    <td className="px-3 py-4 font-bold text-primary">Demo Env</td>
                    <td className="px-3 py-4">Single VM</td>
                    <td className="px-3 py-4 text-center">1</td>
                    <td className="px-3 py-4">Ubuntu 24.04+</td>
                    <td className="px-3 py-4 font-mono">6</td>
                    <td className="px-3 py-4 font-mono">16</td>
                    <td className="px-3 py-4">250 GB</td>
                    <td className="px-3 py-4">Internal/Monitoring</td>
                    <td className="px-3 py-4 font-mono">12001, 11800</td>
                    <td className="px-3 py-4">Docker/Compose</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Docker Installation */}
          <div className="scroll-mt-24" id="docker-install">
            <h3 className="text-lg font-bold text-text-main dark:text-gray-200 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
              1.2 Docker & Docker Compose Installation (Offline)
            </h3>
            <p className="text-sm text-text-muted mb-3">
              Install Docker and Docker Compose using the offline packages provided in the bundle from Apmosys Share point.
            </p>
            <ul className="list-disc ml-6 text-sm text-text-muted mb-3">
              <li>Docker-offline.tar.gz</li>
            </ul>

            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Step 1 — Extract bundle</p>
                <CodeBlock code={`tar -zxvf Docker-offline.tar.gz`} />
              </div>

              <div>
                <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Step 2 — Navigate to package directory</p>
                <CodeBlock code={`cd docker-offline/debs`} />
              </div>

              <div>
                <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Step 3 — Install Docker packages</p>
                <CodeBlock code={`sudo dpkg -i *.deb`} />
              </div>

              <div>
                <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Step 4 — Enable and start Docker</p>
                <CodeBlock code={`sudo systemctl enable --now docker
sudo systemctl start docker
sudo systemctl status docker`} />
              </div>

              <div>
                <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Step 5 — Add current user to docker group</p>
                <CodeBlock code={`sudo usermod -aG docker $USER`} />
              </div>

              <div>
                <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Step 6 — Restart Docker services</p>
                <CodeBlock code={`sudo systemctl restart docker
sudo systemctl restart docker.socket`} />
              </div>
            </div>

            <h3 className="text-lg font-bold text-text-main dark:text-gray-200 mb-3 flex items-center gap-2 mt-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
              Verification
            </h3>
            <div className="bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> Logout and login again after Docker group assignment for changes to take effect.
              </p>
            </div>
            <CodeBlock code={`docker --version
docker compose version
docker ps`} />
          </div>
        </div>
      </section>

{/* DB Layer */}
<section className="scroll-mt-24" id="db-layer">
  <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3 mt-8">
    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-900/50">
      <span className="material-symbols-outlined text-[24px]">database</span>
    </span>
    PART 1: Database Layer Installation
  </h2>

  <p className="text-sm text-text-muted mb-4 font-medium">
    This section covers loading:
  </p>
  <ul className="list-disc ml-6 text-sm text-text-muted mb-6">
    <li>The database Docker images</li>
    <li>Deploying Elasticsearch & MySQL</li>
  </ul>

  <ol className="space-y-6 list-decimal list-outside marker:text-primary marker:font-bold text-text-muted dark:text-gray-400 text-sm">
    <li>
      <h3 className="text-lg font-bold text-text-main dark:text-gray-200 mb-3">Loading the Docker Images of Database</h3>
      <p className="text-sm text-text-muted mb-2 font-medium">Download the below files from provided Apmosys share point</p>
      <ul className="list-disc ml-6 text-sm text-text-muted mb-3">
        <li>netraa-db-bundle-V1.0.0.tar.gz</li>
        <li>ES.zip</li>
        <li>MYSQL.zip</li>
      </ul>
      <p className="text-sm text-text-muted mb-2 font-medium">Load the Netraa db bundle and validate.</p>
      <CodeBlock code={`# Load database images
docker load -i netraa-db-bundle-V1.0.0.tar.gz
# Validate images
docker images`} />
    </li>

    <li>
      <h4 className="text-base font-bold text-text-main dark:text-white mb-2">Elasticsearch Deployment</h4>
      <ul className="list-disc list-inside marker:text-primary marker:font-bold text-text-muted mb-3">
        <li className="mb-3">Unzip the file
          <CodeBlock code={`unzip ES.zip`} />
        </li>
        <li className="mb-3 mt-3">Open the folder "ES" and deploy the container
          <CodeBlock code={`docker compose up -d`} />
        </li>
        <li className="mb-3 mt-3">Check whether the container is up and running
          <CodeBlock code={`docker ps`} />
        </li>
      </ul>
    </li>

    <li>
      <h4 className="text-base font-bold text-text-main dark:text-white mb-2">MySQL Deployment</h4>
      <ul className="list-disc list-inside marker:text-primary marker:font-bold text-text-muted mb-3">
        <li className="mb-3">Unzip the file
          <CodeBlock code={`unzip MYSQL.zip`} />
        </li>
        <li className="mb-3 mt-3">Open the folder "MYSQL" and deploy the container
          <CodeBlock code={`docker compose up -d`} />
        </li>
        <li className="mb-3 mt-3">Check whether the container is up and running
          <CodeBlock code={`docker ps`} />
        </li>
      </ul>
    </li>
  </ol>
</section>

{/* AOP Layer */}
<section className="scroll-mt-24" id="aop-layer">
  <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3 mt-8">
    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-900/50">
      <span className="material-symbols-outlined text-[24px]">database</span>
    </span>
    PART 2: AOP Layer Installation
  </h2>

  <p className="text-sm text-text-muted mb-2">
    The AOP (Agent Orchestration Platform) layer manages agent communication and processing. This layer depends on a healthy Database Layer.
  </p>

  <ol className="space-y-6 list-decimal list-outside marker:text-primary marker:font-bold text-text-muted dark:text-gray-400 text-sm">
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Download the below files from provided Apmosys share point</p>
      <ul className="list-disc ml-6 text-sm text-text-muted mb-3">
        <li>Netraa-AOP.zip</li>
        <li>netraa-aop-aop.tar</li>
      </ul>
     <p className="text-sm text-text-muted mb-2 font-medium">Load Docker images</p>
      <CodeBlock code={`docker load -i netraa-aop-aop.tar`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Extract and open the AOP bundle</p>
      <CodeBlock code={`unzip Netraa-AOP.zip
cd Netraa-AOP/`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Update Elasticsearch IP in configuration</p>
      <p className="text-sm text-text-muted mb-2">Navigate to the configuration file:</p>
      <CodeBlock code={`Netraa-AOP/netraa-bin/config/application.yml`} />
      <p className="text-sm text-text-muted mb-2">Locate the following section and update <code className="text-xs font-mono">clusterNodes</code> with your Elasticsearch / DB IP:</p>
      <CodeBlock code={`storage:
  selector: \${AOP_STORAGE:elasticsearch}
  elasticsearch:
    clusterNodes: <ELASTICSEARCH_IP>:9200`} />
      <p className="text-sm text-text-muted mt-2">Example:</p>
      <CodeBlock code={`clusterNodes: 192.168.12.93:9200`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Start the application</p>
      <CodeBlock code={`cd Netraa-AOP/netraa-bin/
docker-compose up -d`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Validate whether all the AOP Services are up and running</p>
      <CodeBlock code={`docker-compose ps`} />
      <p className="text-sm text-text-muted mt-2">
        Ensure the AOP container is running on the external port <strong>11800</strong>.
      </p>
    </li>
  </ol>
</section>

{/* APM Layer */}
<section className="scroll-mt-24" id="apm-layer">
  <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3 mt-8">
    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-900/50">
      <span className="material-symbols-outlined text-[24px]">monitoring</span>
    </span>
    PART 3: Netraa APM Layer Installation
  </h2>

  <p className="text-sm text-text-muted mb-2">
    Netraa APM provides application monitoring, metrics, and visualization capabilities. This layer depends on both the Database and AOP layers.
  </p>

  <ol className="space-y-6 list-decimal list-outside marker:text-primary marker:font-bold text-text-muted dark:text-gray-400 text-sm">
    <li>
      <p className="text-sm text-text-muted font-semibold mb-2 font-medium">Download the below files from provided Apmosys share point</p>
      <ul className="list-disc ml-6 text-sm text-text-muted mb-3">
        <li>Netraa-APM-Minimal.zip</li>
      </ul>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mt-3 mb-2">Extract and open the APM bundle</p>
      <CodeBlock code={`unzip Netraa-APM-Minimal.zip
cd Netraa-APM-Minimal/`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mt-3 mb-2">Load Docker images</p>
      <CodeBlock code={`docker load -i netraa-apm-minimal.tar`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mt-3 mb-2">Update .env configuration</p>
      <p className="text-sm text-text-muted mb-2">
        The <code className="text-xs font-mono">.env</code> file can't be seen directly. So simply run the below command:
      </p>
      <CodeBlock code={`vim .env
# or
nano .env`} />
      <p className="text-sm text-text-muted mb-2">Paste the below content with respective IPs:</p>
      <CodeBlock code={`DB_USER=root
DB_PASS=ApMoSys@123
MYSQL_URL=jdbc:mysql://{IP_of_where_the_Mysql_is_deployed}:3306/NETRAA?useSSL=false
ELASTIC_HOST={IP_of_where_the_Elastic_is_deployed}
ELASTIC_PORT=9200
REGISTRY_URL=http://{Ip_of_where_the_APM_service_is_hosted}:8761/eureka/
ELASTIC_INDEX_PATTERN=NETRAA
KAFKA_SERVER={Ip_of_where_the_APM_service_is_hosted}:9092
LICENSE_PATH=/app/License/
REDIS_HOST={Ip_of_where_the_APM_service_is_hosted}
REDIS_PORT=6379
TOMCAT_9=http://{Ip_of_where_the_APM_service_is_hosted}:12010
TOMCAT_10=http://{Ip_of_where_the_APM_service_is_hosted}:12020
API_GATEWAY_HOST={Ip_of_where_the_APM_service_is_hosted}`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Start the application</p>
      <CodeBlock code={`# Start APM main application
cd Netraa-APM-Minimal/Netraa/
docker compose up -d

# Start Tomcat 9 service
cd ../tomcat-9
docker compose up -d

# Start Tomcat 10 service
cd ../tomcat-10
docker compose up -d`} />
    </li>
    <li>
      <p className="text-sm font-semibold text-text-main dark:text-gray-300 mb-2">Validate whether all the APM Services are up and running</p>
      <CodeBlock code={`docker-compose ps`} />
    </li>
  </ol>
</section>

{/* License Activation */}
<section className="scroll-mt-24 pb-20 border-b border-border-color dark:border-border-dark" id="license-activation">
  <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3 mt-8">
    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-900/50">
      <span className="material-symbols-outlined text-[24px]">verified_user</span>
    </span>
    Netraa APM UI Access and License Activation
  </h2>

  <div className="prose dark:prose-invert max-w-none">
    <p className="text-sm text-text-muted mb-4">
      The license must be activated on the Netraa APM server before logging into the Netraa UI for the first time.
    </p>

    <ol className="list-decimal list-outside ml-5 text-sm text-text-muted">
  <li className="mb-3">
    Login to the Netraa APM installed server & download the below zip file from APMOSYS SharePoint:
        <div className="mt-2">
          <CodeBlock code={`licence.zip`} />
        </div>
  </li>

  <li className="mb-3">
    Unzip the licence.zip file:
    <div className="mt-2">
      <CodeBlock
        code={`unzip licence.zip`}
      />
      <CodeBlock code={`cd licence`}
      />
    </div>
  </li>

  <li className="mb-3">
    Verify the presence of the activation script:
    <div className="mt-2">
      <CodeBlock code={`activate-license.sh`} />
    </div>
  </li>

  <li className="mb-3">
    Run the script using the provided license string:
    <div className="mt-2">
      <CodeBlock code={`sh activate-license.sh <LICENSE_STRING>`} />
    </div>
  </li>

  <li className="mb-3">The script validates the license string.</li>

  <li className="mb-3">
    Once the license is generated successfully, Netraa UI login will be enabled.
  </li>

  <li className="mb-3">
    Open your browser and navigate to{" "}
    <code className="text-primary font-mono bg-orange-50 dark:bg-orange-900/20 px-1 rounded">
      http://&lt;server-ip&gt;:12001
    </code>{" "}
    and login using the default credentials.
  </li>
</ol>
  </div>
</section>

    </div>
  </main>
);
}